import React, { useState, useEffect } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Button,
  Badge,
  Text,
  Tooltip,
  Spinner,
  Subtitle1,
  Subtitle2
} from '@fluentui/react-components';
import {
  Eye24Regular,
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  InfoRegular,
  InfoFilled,
  bundleIcon,
} from '@fluentui/react-icons';

import { Tender } from '../../models/tendering';
import { PlacementDoc } from '../../models/placements';

import { useAppContext } from '../../AppContext';
import ContactTender from './PlacementDialogs/ContactTender';
import { updatePlacement } from '../../services/placementService';
import DenyTender from './PlacementDialogs/DenyTender';
import { useNavigate } from 'react-router-dom';
import TenderSettingsDrawer from '../TenderSettings/TenderSettingsDrawer';

interface PlacementsListComponentProps {
  tenderList: Tender[];
  placement: PlacementDoc;
  placementDocID: string;
  onPlacementUpdate?: (updatedPlacement: PlacementDoc) => void;
  disableTenderClick?: boolean;
}

const InfoIcon = bundleIcon(InfoFilled, InfoRegular)

export const PlacementsListComponent: React.FC<PlacementsListComponentProps> = ({
  tenderList,
  placement,
  placementDocID,
  onPlacementUpdate,
  disableTenderClick = false
}) => {
  const hasDoublePrices = tenderList.some(t => t.priceInit !== undefined && t.priceExt !== undefined);
  const [displayedColumns, setDisplayedColumns] = useState<string[]>(['', 'name', 'price', 'ranking', 'status', 'action']);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const {user} = useAppContext(); 
  const [requestOpen, setRequestOpen] = useState(false);
  const [isContactTenderOpen, setIsContactTenderOpen] = useState(false);
  const [isDenyTenderOpen, setIsDenyTenderOpen] = useState(false);
  const [tenderModal, setTenderModal] = useState<Tender | null>(null);
  const [tenderDrawerOpen, setTenderDrawerOpen] = useState(false);

  const { featurePermissions } = useAppContext();

  useEffect(() => {
    const baseColumns = ['', 'name'];
    if (hasDoublePrices) {
      baseColumns.push('priceInit', 'priceExt');
    } else {
      baseColumns.push('price');
    }
    baseColumns.push('ranking', 'status', 'action');
    setDisplayedColumns(baseColumns);
  }, [hasDoublePrices]);

  useEffect(() => {
    setRequestOpen(new Date(placement.answerDate) > new Date() && placement.status == 'sent');
    if(!requestOpen && placement.status === 'sent'){
      setClosedRankingTarget();
    }
  }, [placement.answerDate, placement.status]);

  const navigate = useNavigate();

  const formatPrice = (price: number | undefined) => {
    if (price === undefined || price === null) return '-';
    return `${price.toLocaleString('sv-SE')} kr`;
  };

  const formatRanking = (tender: Tender) => {
    if (tender.rankingTarget !== undefined) {
      return tender.rankingTarget ? 'Ja' : 'Nej';
    }
    return '-';
  };

  const getStatusBadge = (tender: Tender) => {
    if( placement.status === 'cancelled'){
      return <Text className="opacity-50" strikethrough>Avbruten</Text>;
    }

    if( tender.denied ) {
      return <Text className="opacity-50" strikethrough>Bortvald</Text>;
    }


    if (tender.placementRequestReceiverStatus === 'Accepted') {
      return requestOpen ? 
      <Text>Besvarad, inväntar slutdatum</Text> : 
      <Text>Intresserad, kontakta verksamheten</Text>;
    } else if (tender.placementRequestReceiverStatus === 'Rejected') {
      return requestOpen ? 
        <Text>Besvarad, inväntar slutdatum</Text> : 
        <Text className="opacity-50" strikethrough>Avböjt förfrågan</Text>;
    } else if (tender.placementRequestReceiverStatus === 'Pending' || placement.status === 'sent') {
      return requestOpen ? 
        <Text>Mottagen, väntar på svar</Text> : 
        <Text>Ej besvarad, kontakta verksamheten</Text>;
    }
    return <Text>Ej skickad</Text>;
  };

  const handleAcceptTender = async (tender: Tender) => {
    setLoadingStatus(true);
    try {
      // Implementation for accepting tender
      console.log('Accepting tender:', tender.tenderID);
      // await placementService.acceptTender(placementDocID, tender.tenderID);
    } catch (error) {
      console.error('Error accepting tender:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleContactClick = (tender: Tender) => {
    setTenderModal(tender);
    setIsContactTenderOpen(true);
  };

  const handleDenyClick = (tender: Tender) => {
    setTenderModal(tender);
    setIsDenyTenderOpen(true);
  };

  const handleViewTender = (tender: Tender) => {
    // Implementation for viewing tender details
    console.log('Viewing tender:', tender.tenderID);
  };

  const undoTender = async (index: number) => {
    setLoadingStatus(true);
    try {
      // Create immutable copies to trigger proper React re-renders
      let updatedTenders = placement.tenders.map((tender, i) => {
        if (i === index) {
          return { ...tender, denied: false };
        }
        return { ...tender };
      });

      // Update rankingTarget immutably
      let foundNewRankingTarget = false;
      updatedTenders = updatedTenders.map((tender) => {
        if (!tender.denied && !foundNewRankingTarget) {
          foundNewRankingTarget = true;
          return { ...tender, rankingTarget: true };
        }
        return { ...tender, rankingTarget: false };
      });

      // Handle closed request ranking target
      if (!requestOpen && placement.status === 'sent') {
        let firstAcceptedFound = false;
        updatedTenders = updatedTenders.map((tender) => {
          const isAcceptedAndNotDenied = 
            (tender.placementRequestReceiverStatus === 'Accepted' || tender.placementRequestReceiverStatus === 'Pending') && 
            !tender.denied;
          
          if (isAcceptedAndNotDenied && !firstAcceptedFound) {
            firstAcceptedFound = true;
            return { ...tender, rankingTarget: true };
          }
          return { ...tender, rankingTarget: false };
        });
      }

      const updatedPlacement: PlacementDoc = {
        ...placement,
        tenders: updatedTenders,
        logs: {
          ...placement.logs,
          tenderChanges: [
            ...placement.logs.tenderChanges,
            {
              date: new Date().toISOString(),
              tenderChange: "Verksamhet väljs tillbaka",
              tender: placement.tenders[index].tenderName,
              userName: user.user.firstName + " " + user.user.lastName,
              filters: placement.activeFilters.filters
            }
          ]
        }
      };

      await updatePlacement(updatedPlacement, placementDocID);
      if (onPlacementUpdate) {
        onPlacementUpdate(updatedPlacement);
      }
    } catch (error) {
      console.error('Error undoing tender:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const setClosedRankingTarget = () => {
    let firstAcceptedFound = false;
    console.log("here");
    
    const updatedTenders = tenderList.map((tender, i) => {
      console.log(tender);
      
      // Check if this is an undenied, accepted tender
      const isAcceptedAndNotDenied = 
        (tender.placementRequestReceiverStatus === "Accepted" || tender.placementRequestReceiverStatus === "Pending") && 
        !tender.denied;
      
      // Only the first undenied accepted tender gets rankingTarget
      if (isAcceptedAndNotDenied && !firstAcceptedFound) {
        firstAcceptedFound = true;
        return { ...tender, rankingTarget: true };
      } else {
        return { ...tender, rankingTarget: false };
      }
    });

    const updatedPlacement = {
      ...placement,
      tenders: updatedTenders
    };

    // Notify parent component of the update
    if (onPlacementUpdate) {
      onPlacementUpdate(updatedPlacement);
    }
  }

  if (!tenderList || tenderList.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Text size={500}>Inga matchande verksamheter hittades</Text>
        <Text>Försök justera dina sökfilter för att hitta fler verksamheter.</Text>
      </div>
    );
  }

  const checkStriketrough = (tender: Tender): boolean => {
    return tender.denied 
      || (tender.placementRequestReceiverStatus === 'Rejected' && !requestOpen)
      || (!tender.placementRequestReceiverStatus && !requestOpen && placement.status === 'sent')
      || placement.status === 'cancelled';
  }

  const handlePlacementUpdate = (updatedPlacement: PlacementDoc) => {
    if(onPlacementUpdate) {
      onPlacementUpdate(updatedPlacement);
    }
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {displayedColumns.includes('') && (
                <TableHeaderCell className="w-10"></TableHeaderCell>
              )}
              {displayedColumns.includes('name') && (
                <TableHeaderCell><Subtitle2>Verksamhetens namn</Subtitle2></TableHeaderCell>
              )}
              {displayedColumns.includes('price') && (
                <TableHeaderCell><Subtitle2>Pris</Subtitle2></TableHeaderCell>
              )}
              {displayedColumns.includes('priceInit') && (
                <TableHeaderCell><Subtitle2>Initialt pris</Subtitle2></TableHeaderCell>
              )}
              {displayedColumns.includes('priceExt') && (
                <TableHeaderCell><Subtitle2>Förlängningspris</Subtitle2></TableHeaderCell>
              )}
              {displayedColumns.includes('ranking') && (
                <TableHeaderCell><Subtitle2>Rangordning</Subtitle2></TableHeaderCell>
              )}
              {displayedColumns.includes('status') && (
                <TableHeaderCell><Subtitle2>Status</Subtitle2></TableHeaderCell>
              )}
              {displayedColumns.includes('action') && (
                <TableHeaderCell></TableHeaderCell>
              )}
            </TableRow>
          </TableHeader>
          
          <TableBody>
            {tenderList.map((tender, index) => (
              <TableRow onClick={disableTenderClick ? undefined : () => { setTenderModal(tender); setTenderDrawerOpen(true); }} key={tender.tenderID || index} className={`group ${disableTenderClick ? '' : 'cursor-pointer'}`}>
                {displayedColumns.includes('') && featurePermissions?.some(fp => fp.action == 'Tendering part/Show tender') && (
                  <TableCell>
                    {!disableTenderClick && (
                      <Tooltip content="Verksamhetens sida" relationship='label'>
                        <Button
                          onClick={() => { setTenderModal(tender); setTenderDrawerOpen(true); }}
                          icon={<InfoIcon />}
                          appearance="subtle"
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          >
                        </Button>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
                {displayedColumns.includes('name') && (
                  <TableCell>
                    <div>
                      <Text className={checkStriketrough(tender) ? 'opacity-50' : ''} strikethrough={checkStriketrough(tender)}>{tender.tenderName}</Text>
                    </div>
                  </TableCell>
                )}
                
                {displayedColumns.includes('price') && (
                  <TableCell>
                    <Text className={checkStriketrough(tender) ? 'opacity-50' : ''} strikethrough={checkStriketrough(tender)}>{formatPrice(tender.price)}</Text>
                  </TableCell>
                )}

                {displayedColumns.includes('priceInit') && (
                  <TableCell>
                    <Text className={checkStriketrough(tender) ? 'opacity-50' : ''} strikethrough={checkStriketrough(tender)}>{formatPrice(tender.priceInit)}</Text>
                  </TableCell>
                )}

                {displayedColumns.includes('priceExt') && (
                  <TableCell>
                    <Text className={checkStriketrough(tender) ? 'opacity-50' : ''} strikethrough={checkStriketrough(tender)}>{formatPrice(tender.priceExt)}</Text>
                  </TableCell>
                )}
                
                {displayedColumns.includes('ranking') && (
                  <TableCell>
                    <Text className={checkStriketrough(tender) ? 'opacity-50' : ''} strikethrough={checkStriketrough(tender)}>{index + 1}</Text>
                  </TableCell>
                )}
                
                {displayedColumns.includes('status') && (
                  <TableCell>
                    {getStatusBadge(tender)}
                  </TableCell>
                )}
                
                {displayedColumns.includes('action') && (
                  <TableCell>
                    {!tender.denied && placement.status !== 'cancelled' && <div className="flex space-x-2">
                      {((!requestOpen && tender.placementRequestReceiverStatus !== "Rejected" && placement.status === 'sent') || (!requestOpen && placement.status !== 'sent'))&& <Button
                        size="medium"
                        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        onClick={(e) => { e.stopPropagation(); handleDenyClick(tender); }}
                      >
                        Välj bort
                      </Button>}
                      {tender.rankingTarget && !requestOpen &&
                        <Button
                          size="medium"
                          appearance="primary"
                          onClick={(e) => { e.stopPropagation(); handleContactClick(tender); }}
                        >
                          Kontakta
                        </Button>}
                    </div>}
                    {tender.denied && placement.status !== 'cancelled' &&
                    (
                    <div>
                      {((!requestOpen && tender.placementRequestReceiverStatus && tender.placementRequestReceiverStatus !== "Rejected" && placement.status === 'sent') || (!requestOpen && placement.status !== 'sent')) && <Button 
                        className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        onClick={(e) => { e.stopPropagation(); undoTender(index); }}>
                        Ångra bortval
                      </Button>}
                    </div>
                  )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {loadingStatus && (
        <div className="flex justify-center">
          <Spinner size="medium" />
        </div>
      )}
      {tenderModal && (
        <ContactTender
          isOpen={isContactTenderOpen}
          onClose={() => setIsContactTenderOpen(false)}
          tender={tenderModal}
          placement={placement}
          placementDocID={placementDocID}
          onPlacementUpdate={handlePlacementUpdate}
        />
      )}
      {tenderModal && (
        <DenyTender
          isOpen={isDenyTenderOpen}
          onClose={() => setIsDenyTenderOpen(false)}
          tender={tenderModal}
          tenderList={tenderList}
          placement={placement}
          placementDocID={placementDocID}
          onPlacementUpdate={handlePlacementUpdate}
        />
      )}
      <TenderSettingsDrawer
        onClose={() => setTenderDrawerOpen(false)}
        tab='info'
        partialTender={tenderModal}
        open={tenderDrawerOpen}
      />
    </div>
  );
};

export default PlacementsListComponent;
